import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import notification from "../../apiConfigs/notifications";
import { ReactComponent as BellIcon } from "../../assets/bell-icon.svg";
import { ReactComponent as Check } from "../../assets/check.svg";
import { ReactComponent as Dot } from "../../assets/dot.svg";
import { ReactComponent as Logo } from "../../assets/rentisity-logo.svg";
import { useRoot } from "../../context/RootProvider";
import Typography from "../../designComponents/Typography";
import { ValueText } from "../../pages/Properties/Details/styles";
import { useSocket } from "../../Provider/SocketProvider";
import { PROFILE_ROUTE } from "../../routes/routePaths";
import {
  clearAxiosConfig,
  setUsersToLocalStorage,
} from "../../utils/apiHelpers";
import { humanReadableDateWithTime } from "../../utils/date";
import requestAPI from "../../utils/requestAPI";
import { Wrapper } from "./styles";
import { resizeImage } from "../../utils/resizeImages";
import useDetectOutsideClick from "../../hooks/useDetectOutsideClick";

const HeadingWrapper = styled.div`
  background-color: white;
  align-items: center;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding-bottom: 16px;
  @media (max-width: 1024px) {
    height: 50px;
  }
`;

const NotificationContainer = styled.div`
  display: flex;
  padding: 8px;
  gap: 10px;
  position: relative;
`;

const NotificationBellWrapper = styled.div`
  padding: 10px 10px;
  border-right-width: 1px;
  border-color: #e5ecf6;
  align-items: center;
  border-style: solid;
  position: relative;
  cursor: pointer;
`;

const Modal = styled.div<{ top?: number; left?: number }>`
  display: flex;
  flex-direction: column;
  min-width: 400px;
  max-width: 600px;
  position: absolute;
  left: ${(props) => props.left}px;
  top: ${(props) => props.top}px;
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 16px;
`;
interface User {
  _id: string;
  avatar: string;
  firstName: string;
  lastName: string;
}

interface Notification {
  _id: string;
  userId: User;
  title: string;
  resourceId: string;
  resourceModel: string;
  isRead: boolean;
  notificationType: string;
  description: string;
  notifyTo: string;
  notifyToRefPath: string;
  createdAt: string; // You can convert this to Date if you prefer to work with Date objects
  updatedAt: string; // Same as above
  __v: number;
}

const PrivateHeader = () => {
  const navigate = useNavigate();
  const bellIconRef = useRef<HTMLDivElement | null>(null);
  const settingDiv = useRef<HTMLDivElement | null>(null);
  const { auth, clearAuth } = useRoot();
  const [openModal, setModal] = useState(false);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [settingModalPosition, setSettingModalPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [notificationModalIsActive, setNotificationModalIsActive] =
    useDetectOutsideClick(bellIconRef, false);
  const { notifications, unreadNotifications } = useSocket();
  const [settingModal, openSettingModal] = useState<boolean>(false);
  const [notificationData, setNotificationData] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState<{
    totalData: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  }>({
    totalPages: 1,
    totalData: 0,
    limit: 7,
    currentPage: 1,
  });
  const [pageNumber, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const getAllNotifications = async () => {
    try {
      setLoading(true);
      const response = await requestAPI(
        notification.getNotifications({
          isRead: null,
          page: pageNumber,
          limit: 7,
        })
      );

      setNotificationData((prevData) => {
        if (pageNumber > 1) {
          const newNotifications = response.data.filter(
            (newNotif: Notification) =>
              !prevData.some((existing) => existing._id === newNotif._id)
          );
          return [...prevData, ...newNotifications];
        } else {
          return response.data;
        }
      });
      setPagination(response?.pagination);
      setLoading(false);
    } catch (error: any) {
      toast.error(`Notifications fetch failed ${error?.message}`);
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string[]) => {
    try {
      await requestAPI(notification.markasRead({ notificationId }));
      setNotificationData((prev: any) => {
        return prev.map((notification: any) => {
          if (notificationId.includes(notification._id)) {
            return { ...notification, isRead: true };
          }
          return notification;
        });
      });
      getAllNotifications();
      toast.success("Marked as read");
    } catch (error: any) {
      toast.error(`Failed to mark as read ${error.message}`);
    }
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      console.log(windowWidth);
    };
  }, []);
  useEffect(() => {
    if (pageNumber <= pagination.totalPages) {
      getAllNotifications();
    }
  }, [pageNumber]);
  useEffect(() => {
    setNotificationData((prev) => {
      // Check if notifications is not empty and is an array or object
      const newNotifications = Array.isArray(notifications)
        ? notifications
        : [notifications];

      return [
        ...newNotifications.filter(
          (newNotification) =>
            !prev.some(
              (existingNotification) =>
                existingNotification._id === newNotification._id
            )
        ),
        ...(prev || []), // Add previous notifications after
      ];
    });
  }, [notifications]);

  useLayoutEffect(() => {
    if (settingDiv.current) {
      const settingsDiv = settingDiv.current.getBoundingClientRect();
      setSettingModalPosition({
        top: settingsDiv.bottom,
        left: settingsDiv.left,
      });
    }

    if (bellIconRef.current) {
      const bellRect = bellIconRef.current.getBoundingClientRect();
      setModalPosition({
        top: bellRect.bottom,
        left: bellRect.left - 250,
      });
    }
  }, [openModal, settingModal]);

  const allIds = notificationData?.map((item, index) => {
    return item._id;
  });
  const handleView = ({
    resourceId,
    type,
  }: {
    resourceId: string;
    type: string;
  }) => {
    if (type === "BOOKING") {
      navigate(`/bookings/${resourceId}`);
    }
    if (type === "PROPERTY") {
      navigate(`/properties/${resourceId}`);
    }
    if (type === "CONTRACT") {
      navigate(`/contract/${resourceId}`);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      Math.abs(scrollHeight - scrollTop - clientHeight) < 1 &&
      !loading &&
      pageNumber < pagination.totalPages
    ) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    console.log("Current page:", pageNumber);
    console.log("Total pages:", pagination.totalPages);
  }, [pageNumber, pagination.totalPages]);

  return (
    <Wrapper style={{ height: windowWidth > 1024 ? "60px" : "50px" }}>
      <HeadingWrapper style={{ height: windowWidth > 1024 ? "75px" : "60px" }}>
        <Logo
          onClick={() => navigate("/")}
          style={{ cursor: "pointer", height: 40 }}
        />
        <NotificationContainer>
          <NotificationBellWrapper
            ref={bellIconRef}
            onClick={() => {
              if (typeof setNotificationModalIsActive !== "boolean") {
                setNotificationModalIsActive(true);
              }
            }}
            style={{ position: "relative" }}
          >
            <BellIcon
              fill="#5F5F5F"
              width={windowWidth > 1024 ? 21 : 18}
              height={windowWidth > 1024 ? 21 : 18}
            />

            {unreadNotifications?.unreadCount > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: -2,
                  right: 3,
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: "#FF4F5A",
                  color: "white",
                  fontSize: 12,
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {unreadNotifications?.unreadCount}
              </div>
            )}
          </NotificationBellWrapper>

          <div
            ref={settingDiv}
            style={{ display: "flex", gap: 10, cursor: "pointer" }}
            onClick={() => {
              openSettingModal(!settingModal);
              setModal(false);
            }}
          >
            <div
              style={{
                width: windowWidth > 1024 ? 50 : 40,
                height: windowWidth > 1024 ? 50 : 40,
                borderRadius: 50,
                borderWidth: 3,
                borderColor: "#806ED8",
                backgroundColor: "#806ED8",
              }}
            >
              <img
                src={
                  resizeImage(auth?.avatar ?? "", "sm") ||
                  "https://e7.pngegg.com/pngimages/84/165/png-clipart-united-states-avatar-organization-information-user-avatar-service-computer-wallpaper-thumbnail.png"
                }
                height={windowWidth > 1024 ? 50 : 40}
                width={windowWidth > 1024 ? 50 : 40}
                style={{
                  borderRadius: 50,
                  borderWidth: 3,
                  borderColor: "#806ED8",
                }}
                alt="Avatar"
              />
            </div>
            <div>
              <Typography
                fontFamily="urbanist"
                style={{ fontSize: 16, fontWeight: 500, color: "#000000" }}
              >
                {auth?.firstName} {auth?.lastName}
              </Typography>
              <Typography
                fontFamily="urbanist"
                style={{ fontSize: 12, fontWeight: 500, color: "#7E7E7E" }}
              >
                {auth?.email}
              </Typography>
            </div>
          </div>
        </NotificationContainer>
        {settingModal && (
          <Modal
            style={{
              top: settingModalPosition.top,
              left: settingModalPosition.left - 250,
              maxHeight: 400,
              gap: 14,
            }}
          >
            <div
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                navigate(PROFILE_ROUTE);
                setModal(false);
                openSettingModal(false);
              }}
            >
              <ValueText style={{ fontSize: 16 }}>Profile</ValueText>
            </div>
            <div
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                // navigate("/login");
                window.location.reload();
                clearAxiosConfig();
                clearAuth();
                setUsersToLocalStorage(null);
                setModal(false);
                openSettingModal(false);
              }}
            >
              <ValueText style={{ fontSize: 16 }}>Logout</ValueText>
            </div>
          </Modal>
        )}
      </HeadingWrapper>

      {notificationModalIsActive && (
        <Modal
          style={{
            top: modalPosition.top,
            left: modalPosition.left,
            maxHeight: "400px",
            overflowY: "auto",
          }}
          onScroll={handleScroll}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography style={{ fontSize: 18, fontWeight: 600 }}>
              Notifications
            </Typography>
            {notificationData.length > 0 &&
              allIds.some((id) =>
                notificationData.find((n) => n._id === id && !n.isRead)
              ) && (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => handleMarkAsRead(allIds)}
                >
                  <Typography style={{ color: "#806ED8", fontSize: 14 }}>
                    Mark all as read
                  </Typography>
                </div>
              )}
          </div>
          {notificationData.length === 0 ? (
            <Typography style={{ textAlign: "center", padding: "20px 0" }}>
              No notifications
            </Typography>
          ) : (
            <>
              {notificationData.map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    handleView({
                      resourceId: item.resourceId,
                      type: item.notificationType,
                    });
                  }}
                  style={{
                    paddingTop: 10,
                    cursor: "-webkit-grab",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <Typography
                      fontFamily="urbanist"
                      style={{
                        color: "#3F3F3F",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Dot
                      fill={item.isRead === true ? "#CCD8EE" : "#806ED8"}
                    ></Dot>
                    <Typography
                      style={{
                        color: "#7E7E7E",
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      Posted {humanReadableDateWithTime(item.createdAt)}
                    </Typography>
                  </div>
                  <Typography
                    style={{ color: "#7E7E7E", fontSize: 14, fontWeight: 500 }}
                  >
                    {item.description}
                  </Typography>

                  <div
                    onClick={() => {}}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 10,
                      cursor: "pointer",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {item.isRead === false && (
                      <>
                        <Check fill="#806ED8"></Check>

                        <Typography
                          style={{
                            color: "#806ED8",
                            fontSize: 12,
                            fontWeight: 500,
                            textDecoration: "underline",
                          }}
                        >
                          <div
                            onClick={() => {
                              handleMarkAsRead([item._id]);
                            }}
                          >
                            Mark as Read
                          </div>
                        </Typography>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ textAlign: "center", padding: "10px 0" }}>
                  <Typography>Loading...</Typography>
                </div>
              )}
              {pagination.currentPage === pagination.totalPages &&
                notificationData.length > 0 && (
                  <div style={{ textAlign: "center", marginTop: 10 }}>
                    <Typography style={{ color: "#7E7E7E", fontSize: 12 }}>
                      No more notifications
                    </Typography>
                  </div>
                )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 16,
                  borderTop: "1px solid #E5ECF6",
                  paddingTop: 12,
                }}
              >
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate("/notifications");
                    setModal(false);
                  }}
                >
                  <Typography style={{ color: "#806ED8", fontSize: 14 }}>
                    View All Notifications
                  </Typography>
                </div>
              </div>
            </>
          )}
        </Modal>
      )}
    </Wrapper>
  );
};

export default PrivateHeader;
